/* File: src/components/replay/ReplayViewer.js */

import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react'; // Added useState, useEffect
import { useNavigate, useLocation } from 'react-router-dom';

// --- Hooks ---
import { useReplayPlayback } from './hooks/useReplayPlayback';
import { useReplayStateSync } from './hooks/useReplayStateSync';
import { useReplayStats } from './hooks/useReplayStats';
import { useReplaySharing } from './hooks/useReplaySharing';
import { useSettings } from '../../contexts/SettingsContext';

// --- UI and Helper Components ---
import PositionGrid from '../game/PositionGrid';
import AudioLetterDisplay from '../game/AudioLetterDisplay';
import MatchButton from '../game/MatchButton';
import ReplayControls from './ReplayControls';
import ConfirmationModal from '../ui/ConfirmationModal';
import ReplayCallToAction from './ReplayCallToAction';
import PublicReplayHeader from '../ui/PublicReplayHeader';
import ReplayStatsPanel from './ReplayStatsPanel';
import FocusMeter from '../dashboard/FocusMeter';
import FeedbackButton from '../feedback/FeedbackButton'; // Import FeedbackButton
import PublicFooter from '../ui/PublicFooter'; // Import the new footer component

// --- Utilities ---
import audioManager from '../../utils/audioManager';
import { formatTime, getReplayDuration } from './utils/replayUtils';

// *** START: Added Safe Area Logic ***
// Helper function to parse CSS pixel values (copied from SidebarLayout)
const parsePixels = (value) => {
    if (typeof value === 'string' && value.endsWith('px')) {
        return parseFloat(value.replace('px', '')) || 0;
    }
    return 0;
};
// *** END: Added Safe Area Logic ***


// Add custom styles to fix the FocusMeter spacing issue
const focusMeterStyles = `
  /* Target the h-8 div within FocusMeter to reduce its height */
  .focus-meter-container .h-8 {
    height: 0.5rem !important; /* Reduce height from 2rem (h-8) to 0.5rem */
  }
`;

// --- Loading/Error Components ---
const ErrorDisplay = ({ message }) => (
    <div className="h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center text-red-700 bg-white p-6 rounded-lg shadow-lg max-w-md">
            <p className="font-semibold mb-2">Error</p>
            <p className="mb-4 text-sm">{message || "An unknown error occurred."}</p>
            <button onClick={() => window.history.back()} className='mt-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors'>Go Back</button>
        </div>
    </div>
);

const LoadingSpinner = ({ message = "Loading..." }) => (
    <div className="h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-primary-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-base sm:text-lg text-gray-600">{message}</p>
        </div>
    </div>
);
// --- End Loading/Error Components ---


// --- Helper Functions ---
const DIFFICULTY_TIERS_SIMPLE = {
    novice: { name: 'Novice', min: 0.00, max: 0.15 },
    apprentice: { name: 'Apprentice', min: 0.20, max: 0.35 },
    adept: { name: 'Adept', min: 0.40, max: 0.55 },
    expert: { name: 'Expert', min: 0.60, max: 0.75 },
    master: { name: 'Master', min: 0.80, max: 0.95 },
};
const getTierFromDifficulty = (difficulty) => {
    const d = difficulty ?? 0;
    for (const tierKey in DIFFICULTY_TIERS_SIMPLE) {
        const tier = DIFFICULTY_TIERS_SIMPLE[tierKey];
        if (d >= tier.min && d <= tier.max) return tier;
    }
    return DIFFICULTY_TIERS_SIMPLE.novice;
};
const getTierName = (tierObj) => {
    if (!tierObj || !tierObj.name) return 'Unknown';
    return tierObj.name.charAt(0).toUpperCase() + tierObj.name.slice(1);
};
const formatCombinedDifficulty = (nBack, difficulty) => {
    const diffValue = Math.max(0, Math.min(0.95, difficulty ?? 0));
    const formattedDifficulty = diffValue.toFixed(2).substring(2);
    return `${nBack}.${formattedDifficulty}`;
};
// --- End Helper Functions ---


const ReplayViewer = ({
    replayData,
    username = 'Player',
    sessionId,
    profileId,
    initialIsPublic = false,
    isPublicView = false, // This prop determines if it's accessed via a public link
    isLocalReplay = false, // This prop determines if it's loaded locally
    onExit,
    authUser, // The currently logged-in user object from Firebase Auth
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const cameFromResults = location.state?.fromResults === true;
    const { settings: appSettings } = useSettings();

    // *** START: Added Safe Area State & Effect ***
    const [isIosStandalone, setIsIosStandalone] = useState(false);
    const [safeAreaTopPx, setSafeAreaTopPx] = useState(0);

    useEffect(() => {
        // Logic copied and adapted from SidebarLayout.js
        if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            // Check PWA standalone mode OR if it's potentially added to homescreen but not reporting true 'standalone' yet
            const standalone = ('standalone' in navigator && navigator.standalone) || (window.matchMedia('(display-mode: standalone)').matches);
            const isStandaloneModeValue = isIOS && standalone;
            setIsIosStandalone(isStandaloneModeValue);

            if (isStandaloneModeValue) {
                if (window.CSS && CSS.supports('top', 'env(safe-area-inset-top)')) {
                    // Create a temporary element to measure the value
                    const tempEl = document.createElement('div');
                    tempEl.style.position = 'fixed';
                    // Use a CSS variable to read the env value
                    tempEl.style.setProperty('--temp-safe-area-top', 'env(safe-area-inset-top)');
                    // Hide the element
                    tempEl.style.visibility = 'hidden'; tempEl.style.height = '0'; tempEl.style.width = '0';
                    document.body.appendChild(tempEl);
                    // Read the computed style
                    const safeAreaValue = getComputedStyle(tempEl).getPropertyValue('--temp-safe-area-top');
                    // Clean up the temporary element
                    document.body.removeChild(tempEl);

                    const topPx = parsePixels(safeAreaValue);
                    console.log(`[ReplayViewer] iOS Standalone Detected. Safe Area Top Raw: '${safeAreaValue}', Parsed Px: ${topPx}`);
                    setSafeAreaTopPx(topPx);
                } else {
                    console.warn('[ReplayViewer] iOS Standalone Detected, but env(safe-area-inset-top) not supported or measurable.');
                }
            }
        }
    }, []); // Run only once on mount
    // *** END: Added Safe Area State & Effect ***

    // --- Prop Validation ---
    if (!replayData || !replayData.events || typeof replayData.startTime !== 'number') {
        console.error("ReplayViewer: Missing or invalid data.", { replayData }); // Keep critical error
        return <ErrorDisplay message="Could not load replay viewer due to missing or invalid data." />;
    }
    const { settings: replaySettings, events, startTime: replayStartTime } = replayData;

    // *** MODIFIED LINE: Read 'difficulty' instead of 'difficultySeed', rename to 'difficultyValue' locally ***
    const { nBack = 2, difficulty: difficultyValue = 0.5, generatedSequence: sequence = [],
            actualTrialCount, totalTrials: totalTrialsFromSettings,
            modality = 'dual' } = replaySettings || {};
    // *** END MODIFIED LINE ***
    const totalTrials = actualTrialCount ?? totalTrialsFromSettings ?? 0;

    // --- Default Voice ID ---
    const defaultVoiceId = useMemo(() => {
        if (audioManager && audioManager.VOICES && typeof audioManager.VOICES === 'object') {
            const voiceKeys = Object.keys(audioManager.VOICES);
            if (voiceKeys.length > 0) { return voiceKeys[0]; }
        }
        console.warn("[ReplayViewer] audioManager.VOICES not available/populated during defaultVoiceId calculation."); // Keep potential config warning
        return null;
    }, []);

    // --- State & Context ---
    const [showReplayHelpers, setShowReplayHelpers] = useState(false);
    const isLoggedIn = !!authUser;

    // --- VIEW TYPE DETERMINATION ---
    const isPublicUnauthenticatedView = !isLoggedIn && isPublicView;
    const viewingOwnReplay = isLoggedIn && !!replayData.userId && authUser.uid === replayData.userId && !!profileId;
    // Calculate isPrivateView:
    // It's private if it's a local replay OR if the user is logged in AND (it's not explicitly a public view OR they own the replay being viewed).
    const isPrivateView = isLocalReplay || (isLoggedIn && (!isPublicView || viewingOwnReplay));

    // --- Refs ---
    const prevTrialIndexRef = useRef(-1);
    const prevPlaybackStateRef = useRef('paused');
    const updateStateForTimeRef = useRef(null);
    const calculateStatsAtTimeRef = useRef(null);
    const onFinishedCallbackRef = useRef(null);

    // --- Derived Values ---
    const totalDuration = useMemo(() => getReplayDuration(events || []), [events]);
    // *** MODIFIED LINE: Use difficultyValue from replaySettings ***
    const currentTier = useMemo(() => getTierFromDifficulty(difficultyValue), [difficultyValue]);
    // *** END MODIFIED LINE ***

    // --- Initialize AudioManager ---
    useEffect(() => {
        if (!defaultVoiceId) return;
        if(audioManager.init()) {
            audioManager.preload({
              soundSetId: 'alphabet',
              voiceId: defaultVoiceId,
            });
        } else {
            console.error("[ReplayViewer] AudioManager initialization failed, cannot preload."); // Keep critical error
        }
        return () => {
          audioManager.stopCurrentSound(); // Cleanup on unmount
        };
      }, [defaultVoiceId]); // Note: removed difficultyValue from here, preload doesn't need specific difficulty

    // --- Custom Hooks ---
    const {
        currentTrialIndex, gridPosition, visualFeedbackState, missedVisualHighlightIndex,
        posResponseFlash, audioLetter, audioFeedbackState, audioResponseFlash,
        updateStateForTime, displayStimuliForTrial, findTrialStartTime, resetStateToStart,
    } = useReplayStateSync(events, replaySettings, replayStartTime);

    const { liveStats, calculateStatsAtTime, resetStats } = useReplayStats(events, replayStartTime);

    // --- Keep Refs Updated with Latest Functions ---
    useEffect(() => { updateStateForTimeRef.current = updateStateForTime; }, [updateStateForTime]);
    useEffect(() => { calculateStatsAtTimeRef.current = calculateStatsAtTime; }, [calculateStatsAtTime]);

    // --- Define onFinished logic ---
    const handlePlaybackFinished = useCallback(() => {
        if (updateStateForTimeRef.current) updateStateForTimeRef.current(totalDuration, false);
        if (calculateStatsAtTimeRef.current) calculateStatsAtTimeRef.current(totalDuration);
        prevPlaybackStateRef.current = 'finished';
    }, [totalDuration]);

    // Update the ref for the finished handler
    useEffect(() => { onFinishedCallbackRef.current = handlePlaybackFinished; }, [handlePlaybackFinished]);

    // --- Define STABLE Callbacks for useReplayPlayback ---
    const stableOnTimeUpdate = useCallback((newTime) => {
        if (updateStateForTimeRef.current) updateStateForTimeRef.current(newTime, false);
        if (calculateStatsAtTimeRef.current) calculateStatsAtTimeRef.current(newTime);
    }, []); // Stable

    const stableOnFinished = useCallback(() => {
        if (onFinishedCallbackRef.current) onFinishedCallbackRef.current();
    }, []); // Stable

    // --- Playback Hook Initialization ---
    const {
        playbackState, currentTime, playbackSpeed,
        togglePlayPause, reset: playbackReset, seek, changeSpeed,
        _setCurrentTime, _setPlaybackState
    } = useReplayPlayback(
        totalDuration,
        stableOnTimeUpdate,
        stableOnFinished
    );

    // --- Resume Logic ---
    const handlePlayPauseWithResume = useCallback(() => {
        const ctx = audioManager.getAudioContext();
        if (ctx && ctx.state === 'suspended') {
          ctx.resume()
            .then(() => {
              togglePlayPause();
            })
            .catch(err => {
              console.error("[ReplayViewer] Failed to resume audio context on play click:", err); // Keep error
            });
        } else if (ctx && ctx.state === 'running') {
           togglePlayPause();
        } else {
          console.warn("[ReplayViewer] AudioContext not available or in unexpected state:", ctx?.state); // Keep warning
          togglePlayPause();
        }
      }, [togglePlayPause]);

    // --- Sharing Hook ---
    const {
        isSharing, shareError, showShareLinkModal, shareLink, isNowPublic, canShare,
        handleShareOrCopy, handleCopyLink, closeShareModal
    } = useReplaySharing(sessionId, profileId, initialIsPublic, isPublicView, isLocalReplay);


    // --- AUDIO PLAYBACK EFFECT ---
    useEffect(() => {
        const currentPlaybackState = playbackState;
        const previousPlaybackState = prevPlaybackStateRef.current;

        if (currentPlaybackState !== 'playing') {
          audioManager.stopCurrentSound();
          prevPlaybackStateRef.current = currentPlaybackState;
          return;
        }

        let trialToPlay = -1;
        const isJustStarted = currentPlaybackState === 'playing' && previousPlaybackState !== 'playing';

        if (isJustStarted) {
            if (currentTrialIndex === 0) {
                trialToPlay = 0;
            }
        }
        else if (currentTrialIndex >= 0 && currentTrialIndex !== prevTrialIndexRef.current) {
           trialToPlay = currentTrialIndex;
        }

        if (trialToPlay >= 0) {
            prevTrialIndexRef.current = trialToPlay; // Update ref before async call

            const audioStimEvent = events.find(
                e => e.type === 'audioStimulusStart' && e.trialIndex === trialToPlay
            );

            if (!audioStimEvent?.letter) {
               prevPlaybackStateRef.current = currentPlaybackState;
               return;
            }

            const ctx = audioManager.getAudioContext();
            if (!ctx || ctx.state !== 'running') {
              console.warn(`[Replay Audio Effect] Skipping play for trial ${trialToPlay}, audio context state is: ${ctx?.state}`); // Keep warning
              prevPlaybackStateRef.current = currentPlaybackState;
              return;
            }

            const voiceId = audioStimEvent.voice || appSettings.selectedVoiceId || defaultVoiceId;
            // *** MODIFIED LINE: Use difficultyValue from replaySettings ***
            const difficultyForPlayback = typeof audioStimEvent.difficulty === 'number'
               ? audioStimEvent.difficulty
               : difficultyValue;
            // *** END MODIFIED LINE ***

            if (!voiceId) {
              console.error(`[Replay Audio Effect] No valid voiceId found for trial ${trialToPlay}. Skipping play.`); // Keep error
              prevPlaybackStateRef.current = currentPlaybackState;
              return;
            }

            audioManager
              .playItem(audioStimEvent.letter, difficultyForPlayback, { voiceId })
              .catch(err => {
                  console.error(`[Replay Audio Effect] PlayItem FAILED for trial ${trialToPlay}:`, err); // Keep error
              });
        }

        // Update previous state ref at the end
        prevPlaybackStateRef.current = currentPlaybackState;

      }, [
        playbackState, currentTrialIndex, currentTime,
        events,
        // *** MODIFIED LINE: Add difficultyValue to dependency array ***
        difficultyValue,
        // *** END MODIFIED LINE ***
        appSettings.selectedVoiceId, defaultVoiceId,
      ]);


    // --- Other CALLBACKS ---
    const handleReset = useCallback(() => {
        playbackReset();
        resetStateToStart();
        resetStats();
        prevTrialIndexRef.current = -1;
        prevPlaybackStateRef.current = 'paused';
        audioManager.stopCurrentSound();
    }, [playbackReset, resetStateToStart, resetStats]);

    const handleStep = useCallback(async (direction) => {
        const resumeSuccess = await audioManager.resume();
        if (!resumeSuccess) console.warn("[Replay Step] Audio context could not be resumed."); // Keep warning

        const wasPlaying = playbackState === 'playing';
        if (wasPlaying) {
             _setPlaybackState('paused');
             audioManager.stopCurrentSound();
        }

        const stepAmount = direction === 'forward' ? 1 : -1;
        let targetTrialIndex = prevTrialIndexRef.current + stepAmount;
        let targetTime = null;
        let newPlaybackState = 'paused';

        if (targetTrialIndex >= totalTrials) {
            targetTrialIndex = totalTrials - 1;
            targetTime = totalDuration;
            newPlaybackState = 'finished';
        } else if (targetTrialIndex < -1) {
            targetTrialIndex = -1;
            targetTime = 0;
        } else if (targetTrialIndex === -1) {
            targetTime = 0;
        } else {
            targetTime = findTrialStartTime(targetTrialIndex);
            if (targetTime === null) {
                 console.warn(`[Replay Step] Could not find start time for trial ${targetTrialIndex}. Aborting step.`); // Keep warning
                 return;
            }
        }

        _setCurrentTime(targetTime);
        if(updateStateForTimeRef.current) updateStateForTimeRef.current(targetTime, true);
        if(calculateStatsAtTimeRef.current) calculateStatsAtTimeRef.current(targetTime);
        displayStimuliForTrial(targetTrialIndex);

        prevTrialIndexRef.current = targetTrialIndex;
        _setPlaybackState(newPlaybackState);
        prevPlaybackStateRef.current = newPlaybackState;

        if (resumeSuccess && direction === 'forward' && targetTrialIndex >= 0 && targetTrialIndex < totalTrials && (modality === 'dual' || modality === 'audio')) {
            const audioStimEvent = events.find(e => e.type === 'audioStimulusStart' && e.trialIndex === targetTrialIndex);
            if (audioStimEvent?.letter) {
                // *** MODIFIED LINE: Use difficultyValue from replaySettings ***
                const difficultyForPlayback = typeof audioStimEvent.difficulty === 'number'
                    ? audioStimEvent.difficulty
                    : difficultyValue;
                // *** END MODIFIED LINE ***
                const voiceId = audioStimEvent.voice || appSettings?.selectedVoiceId || defaultVoiceId;
                if (voiceId) {
                    audioManager.playItem(audioStimEvent.letter, difficultyForPlayback, { voiceId })
                        .catch(error => console.error(`[Replay Step Audio] Error playing item:`, error)); // Keep error
                } else {
                     console.warn(`[Replay Step] Could not determine voiceId for trial ${targetTrialIndex}`); // Keep warning
                }
            }
        } else if (!resumeSuccess && targetTrialIndex >= 0) {
             console.warn(`[Replay Step] Audio context not running, skipping audio playback for trial ${targetTrialIndex}`); // Keep warning
        }

    }, [
        playbackState, totalTrials, totalDuration, modality,
        // *** MODIFIED LINE: Add difficultyValue to dependency array ***
        difficultyValue,
        // *** END MODIFIED LINE ***
        events,
        appSettings?.selectedVoiceId, defaultVoiceId, findTrialStartTime,
        _setCurrentTime, _setPlaybackState, displayStimuliForTrial
    ]);

    const handleStepForward = useCallback(() => handleStep('forward'), [handleStep]);
    const handleStepBackward = useCallback(() => handleStep('backward'), [handleStep]);
    const toggleReplayHelpers = useCallback(() => setShowReplayHelpers(prev => !prev), []);

    // --- Derived Values for Rendering ---
    const missedPositionToHighlight = useMemo(() => ( sequence && missedVisualHighlightIndex !== null && missedVisualHighlightIndex >= 0 && sequence[missedVisualHighlightIndex] ) ? sequence[missedVisualHighlightIndex].position : null, [sequence, missedVisualHighlightIndex]);

    // --- Reusable Replay Card Content Definition ---
    const replayCardContentJSX = (
        <>
            {/* Unified Card Header Content */}
            <div className="p-6">
                {/* Removed Test Audio Button */}
                <div className="flex justify-between items-center mb-3">
                    <div className="text-sm text-gray-600">
                        <span className="font-medium">Replay by:</span>{' '}
                        <span className="text-primary-600 font-semibold">{username || 'Unknown User'}</span>
                        {isPrivateView && viewingOwnReplay && (
                            <span className="ml-2 text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Your Replay</span>
                        )}
                    </div>
                    <div className="text-center font-medium text-gray-800 text-lg">
                        Trial: {currentTrialIndex >= 0 ? currentTrialIndex + 1 : '-'} / {totalTrials}
                    </div>
                </div>
                <hr className="my-3 border-gray-200" />
                <div className="flex justify-between items-center mb-2">
                     <div>
                         <span className="font-medium text-lg text-gray-800">{nBack}-back</span>
                         <span className={`ml-2 font-semibold px-2 py-0.5 rounded-md text-sm ${
                             currentTier.name === 'Novice' ? 'bg-blue-100 text-blue-700' :
                             currentTier.name === 'Apprentice' ? 'bg-green-100 text-green-700' :
                             currentTier.name === 'Adept' ? 'bg-yellow-100 text-yellow-700' :
                             currentTier.name === 'Expert' ? 'bg-orange-100 text-orange-700' :
                             currentTier.name === 'Master' ? 'bg-red-100 text-red-700' :
                             'bg-gray-100 text-gray-700'
                         }`}>
                             {getTierName(currentTier)}
                         </span>
                     </div>
                     <div className="text-right">
                         <span className="text-sm text-gray-600">Current Level: </span>
                         <span className="font-medium text-gray-700 text-sm font-mono">
                             {/* *** MODIFIED LINE: Use difficultyValue from replaySettings *** */}
                             {formatCombinedDifficulty(nBack, difficultyValue)}
                             {/* *** END MODIFIED LINE *** */}
                         </span>
                     </div>
                </div>
                <div className="focus-meter-container">
                    <FocusMeter
                        nBack={nBack}
                        // *** MODIFIED LINE: Use difficultyValue from replaySettings ***
                        difficulty={difficultyValue}
                        // *** END MODIFIED LINE ***
                        hideAdvancementInfo={true}
                        hideCurrentLevelDisplay={true}
                        hideTierDisplay={true}
                    />
                </div>
                 <hr className="mt-3 mb-4 border-gray-200" />
            </div>

            {/* Game Content Area */}
            <div className="px-6 pb-6 pt-0">
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                    <div className="flex justify-center w-full max-w-[280px] sm:max-w-[320px] lg:w-72 lg:max-w-none">
                        <PositionGrid
                            isReplayMode={true} activePosition={gridPosition} feedbackState={visualFeedbackState} missedHighlightPositionIndex={missedPositionToHighlight}
                            showNoviceHelp={showReplayHelpers} sequence={sequence} currentTrial={currentTrialIndex} nBack={nBack}
                        />
                    </div>
                    {showReplayHelpers && modality !== 'visual' && (
                         <div className="w-full sm:w-auto flex items-center justify-center sm:ml-4 md:ml-6 lg:ml-8">
                            <AudioLetterDisplay
                                isReplayMode={true} activeLetter={audioLetter} feedbackState={audioFeedbackState} showNoviceHelp={showReplayHelpers}
                                sequence={sequence} currentTrial={currentTrialIndex} nBack={nBack}
                                // *** MODIFIED LINE: Pass difficultyValue from replaySettings ***
                                difficulty={difficultyValue}
                                // *** END MODIFIED LINE ***
                            />
                        </div>
                    )}
                </div>
                {/* Match Buttons Section */}
                <div className="grid grid-cols-2 gap-4 mt-6 h-14">
                    {/* Position Match Button */}
                    <MatchButton
                        label="Position Match"
                        keyHint="←"
                        onClick={() => {}} // Non-interactive in replay
                        // Use 'acknowledged' for the flash effect when posResponseFlash is true,
                        // otherwise show the longer-term visualFeedbackState (correct/incorrect/missed)
                        feedbackState={posResponseFlash ? 'acknowledged' : visualFeedbackState}
                        disabled={false} // No longer visually disabled
                        aria-hidden="true"
                    />
                    {/* Audio Match Button */}
                    <MatchButton
                        label="Audio Match" keyHint="→" onClick={() => {}} // Non-interactive
                        feedbackState={audioResponseFlash ? 'acknowledged' : audioFeedbackState} // Use 'acknowledged' for flash
                        disabled={modality === 'visual'} // Only disable if modality is purely visual
                        aria-hidden="true" />
                </div>
            </div>
        </>
    );


    // *** START: Calculate Dynamic Styles for Safe Area ***
    const mainContainerStyle = useMemo(() => (
        isIosStandalone ? {
            paddingTop: `${safeAreaTopPx}px`,
            // Apply bottom padding directly using env variable
            paddingBottom: `env(safe-area-inset-bottom)`
        } : {}
    ), [isIosStandalone, safeAreaTopPx]);
    // *** END: Calculate Dynamic Styles for Safe Area ***

    // --- Main Component Render ---
    return (
        // Apply dynamic styles to the outermost container
        <div
            className="min-h-screen bg-gray-100 flex flex-col"
            style={mainContainerStyle} // Apply safe area padding here
        >
            <style>{focusMeterStyles}</style>
            {isPublicUnauthenticatedView && <PublicReplayHeader />}

            {/* Main Content Wrapper (flex-grow handles vertical space) */}
            <div className="w-full max-w-7xl mx-auto px-4 py-6 flex-grow flex flex-col"> {/* Ensure flex-grow and flex-col */}

                {/* Back Buttons */}
                {isPrivateView && !isPublicView && (
                    <div className="mb-4 flex-shrink-0"> {/* Add flex-shrink-0 */}
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 inline-flex items-center"
                            aria-label="Back to dashboard"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back to Dashboard
                        </button>
                    </div>
                )}
                 {cameFromResults && (
                    <div className="mb-4 flex-shrink-0"> {/* Add flex-shrink-0 */}
                        <button
                            onClick={() => navigate(-1)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center"
                            aria-label="Back to game results" >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back to Results
                        </button>
                    </div>
                 )}

                {/* Main Layout (columns) - Allow this section to grow */}
                <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6 flex-grow"> {/* Add flex-grow here */}
                    {/* Left Column */}
                    <div className="w-full lg:flex-1">
                        <div className="bg-white rounded-lg shadow-lg w-full relative mb-4 lg:mb-0">
                            {replayCardContentJSX}
                            {/* --- START: MODIFIED Mobile controls footer --- */}
                            <div className="lg:hidden px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                                {/* Container for controls row and progress bar column */}
                                <div className="flex flex-col gap-3"> {/* Changed from sm:flex-row */}

                                    {/* --- START: Commented out Show Helpers Button --- */}
                                    {/*
                                    <button
                                        onClick={toggleReplayHelpers}
                                        className={`w-full sm:w-auto px-3 py-2 text-xs rounded-md font-medium transition-colors border mb-2 sm:mb-0 ${
                                            showReplayHelpers
                                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                                        }`}
                                    >
                                        {showReplayHelpers ? 'Hide Helpers' : 'Show Helpers'}
                                    </button>
                                    */}
                                    {/* --- END: Commented out Show Helpers Button --- */}


                                    {/* Row for Controls and Share Button */}
                                    <div className="flex flex-row items-center gap-3 w-full">
                                        {/* ReplayControls now takes available space */}
                                        <div className="flex-grow">
                                            <ReplayControls
                                                state={playbackState}
                                                currentTime={currentTime}
                                                totalDuration={totalDuration}
                                                speed={playbackSpeed}
                                                onPlayPause={handlePlayPauseWithResume}
                                                onSpeedChange={changeSpeed}
                                                onReset={handleReset}
                                                onStepBackward={handleStepBackward}
                                                onStepForward={handleStepForward}
                                                // *** MODIFIED LINE: Pass difficultyValue from replaySettings ***
                                                difficultySeed={difficultyValue} // Prop name is difficultySeed, pass correct value
                                                // *** END MODIFIED LINE ***
                                                defaultVoiceId={defaultVoiceId}
                                            />
                                        </div>
                                        {/* Share button takes remaining necessary space */}
                                        {canShare && (
                                            <div className="flex-shrink-0"> {/* Prevent share button from growing */}
                                                <button
                                                    className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center ${
                                                        isSharing
                                                            ? 'bg-gray-400 cursor-wait'
                                                            : isNowPublic
                                                            ? 'bg-gray-500 hover:bg-gray-600'
                                                            : 'bg-green-500 hover:bg-green-600'
                                                    }`}
                                                    onClick={handleShareOrCopy}
                                                    disabled={isSharing}
                                                    title={
                                                        isNowPublic
                                                            ? 'Copy public share link'
                                                            : 'Make replay public for anyone to view'
                                                    }
                                                >
                                                    <i className="fas fa-share-nodes mr-2"></i>
                                                    {isSharing
                                                        ? 'Processing...'
                                                        : isNowPublic
                                                        ? 'Copy Link' // Shortened text for mobile
                                                        : 'Share'}    {/* Shortened text for mobile */}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {shareError && canShare && (
                                        <p className="text-center text-red-500 text-xs mt-1">{shareError}</p> // Reduced margin
                                    )}

                                    {/* Progress Bar (Moved below controls) */}
                                    <div className="w-full"> {/* Removed mb-2 */}
                                        <div className="bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                            <div
                                                className="bg-primary-600 h-full rounded-full transition-width duration-100 ease-linear"
                                                style={{
                                                    width: `${
                                                        totalDuration > 0
                                                            ? (currentTime / totalDuration) * 100
                                                            : 0
                                                    }%`,
                                                }}
                                            ></div>
                                        </div>
                                        <div className="mt-1 flex justify-end text-xs text-gray-500">
                                            <div>
                                                {formatTime(currentTime)} / {formatTime(totalDuration)}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            {/* --- END: MODIFIED Mobile controls footer --- */}
                        </div>
                    </div>
                    {/* Right Column */}
                    <div className="w-full lg:w-96 lg:flex-shrink-0 mt-6 lg:mt-0 space-y-6">
                        {/* Desktop Controls Card */}
                        <div className="hidden lg:block bg-white rounded-lg shadow-md border border-gray-200 p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-sm font-semibold text-gray-700">Replay Controls</h3>
                                {/* --- START: Commented out Show Helpers Button (Desktop) --- */}
                                {/*
                                <button
                                    onClick={toggleReplayHelpers}
                                    className={`px-3 py-1 text-xs rounded-md font-medium transition-colors border ${
                                        showReplayHelpers
                                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                                            : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                                    }`}
                                >
                                    {showReplayHelpers ? 'Hide Helpers' : 'Show Helpers'}
                                </button>
                                */}
                                {/* --- END: Commented out Show Helpers Button (Desktop) --- */}
                            </div>
                            <div className="mb-3 bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-primary-600 h-full rounded-full transition-width duration-100 ease-linear" style={{ width: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%` }} ></div>
                            </div>
                            <div className="mb-3 flex justify-end text-xs text-gray-500">
                                <div>{formatTime(currentTime)} / {formatTime(totalDuration)}</div>
                            </div>
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-full">
                                    <ReplayControls
                                        state={playbackState} currentTime={currentTime} totalDuration={totalDuration} speed={playbackSpeed}
                                        onPlayPause={handlePlayPauseWithResume}
                                        onSpeedChange={changeSpeed} onReset={handleReset}
                                        onStepBackward={handleStepBackward} onStepForward={handleStepForward}
                                        // *** MODIFIED LINE: Pass difficultyValue from replaySettings ***
                                        difficultySeed={difficultyValue} // Prop name is difficultySeed, pass correct value
                                        // *** END MODIFIED LINE ***
                                        defaultVoiceId={defaultVoiceId}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Share Button Card */}
                        {canShare && (
                            <div className="hidden lg:block bg-white rounded-lg shadow-md border border-gray-200 p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Share Replay</h3>
                                <button
                                    className={`w-full px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center ${
                                        isSharing ? 'bg-gray-400 cursor-wait' :
                                        (isNowPublic ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-500 hover:bg-green-600')
                                    }`}
                                    onClick={handleShareOrCopy} disabled={isSharing}
                                    title={isNowPublic ? 'Copy public share link' : 'Make replay public for anyone to view'} >
                                    <i className="fas fa-share-nodes mr-2"></i>
                                    {isSharing ? 'Processing...' : (isNowPublic ? 'Copy Share Link' : 'Share Publicly')}
                                </button>
                                {shareError && <p className="text-center text-red-500 text-xs mt-2">{shareError}</p>}
                            </div>
                        )}
                        {isPublicUnauthenticatedView && <ReplayCallToAction />}
                        <ReplayStatsPanel stats={liveStats} />
                    </div>
                </div>
            </div> {/* End Main Content Wrapper */}

            {/* Share Link Modal (usually positioned fixed/absolute, less affected by container padding) */}
            <ConfirmationModal
                isOpen={showShareLinkModal} title="Share Replay Link" message="This replay has been made public. Anyone with this link can view your replay session."
                confirmText="Copy Link"
                cancelText="Close"
                onConfirm={handleCopyLink}
                onClose={closeShareModal} // *** CHANGE THIS LINE from onCancel to onClose ***
                isDestructive={false} >
                <div className="mt-4">
                    <label htmlFor="shareLinkInput" className="block text-sm font-medium text-gray-700 mb-1">Shareable Link:</label>
                    <input
                        id="shareLinkInput" type="text" readOnly value={shareLink}
                        className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-text"
                        onFocus={(e) => e.target.select()} aria-label="Shareable replay link"
                    />
                </div>
            </ConfirmationModal>

            {/* Render the Public Footer INSIDE the main padded container */}
            {isPublicUnauthenticatedView && (
                <div className="w-full flex-shrink-0"> {/* Ensure it doesn't grow unnecessarily */}
                    <PublicFooter />
                </div>
            )}

            {/* Render the Feedback Button OUTSIDE the main container IF it uses fixed positioning */}
            {/* Note: If FeedbackButton is fixed, it might need its OWN safe area bottom adjustment */}
            {isPrivateView && !isPublicView && <FeedbackButton />}

        </div> // End outermost container
    );
};

export default ReplayViewer;